import React, {
    useEffect,
    useRef
} from 'react';
import {
    AbstractHorizontalContainer,
    HorizontalNoteContainer,
    NoteRangeType
} from '../../../model/containers';
import {
    calculateNormalizedBarWidth,
    getLeftNearestGridPoint,
    NOTE_HEIGHT,
    NOTE_WIDTH,
    positionToTick,
    tickToPosition
} from '../../../render/renderUtils';
import { HarmonicPredicates } from '../../../utils/harmonyHighlight';
import {
    Note,
    NoteDuration,
    TimeSignature
} from '../../../model/core';
import {
    NoteSelectionGroup,
    SelectionGeometry
} from '../../../utils/NoteSelectionGroup';
import {
    drawPianoRollBackground,
    drawPianoRollGridlines
} from '../../../render/canvasRenderer';
import {
    useNoteSelectionGroups,
    useSetNoteSelectionGroups
} from '../../../stores/InspectorPanelStore';
import { useCopyBuffer } from '../../../stores/CopyBufferStore';
import { useSetCopyDropOverlayGeometry } from '../../../stores/CopyDropOverlayStore';
import { CopyBuffer } from '../../../utils/copyBuffer';
import { useZoomFactor } from '../../../stores/ZoomStore';
import {
    useWorkspaceUIState,
    workspaceStore
} from '../../../stores/WorkspaceStore';
import { GridResolution } from '../../../model/grid';
import { calculateSelectedNotes, Selection } from '../../../utils/selection';


function pluckCoordinates(
    e: React.MouseEvent<HTMLDivElement, MouseEvent>
): {
    clientX: number,
    boundingX: number,
    clientY: number,
    boundingY: number
} {
    const boundingRect = e.currentTarget.getBoundingClientRect();
    return {
        clientX: e.clientX,
        boundingX: boundingRect.x,
        clientY: e.clientY,
        boundingY: boundingRect.y
    };
}

function convertCoordinates(
    noteRange: NoteRangeType,
    coodinates: { clientX: number, boundingX: number, clientY: number, boundingY: number },
    zoomFactor: number = 1
) {
    const tick = 0 | positionToTick(NOTE_WIDTH, zoomFactor, coodinates.clientX - coodinates.boundingX);
    const note = 0 | (107 - (coodinates.clientY - coodinates.boundingY) / NOTE_HEIGHT) - (107 - noteRange.max);
    return { tick: tick, note: note };
}

function normalizeBoxCoordinates(selection: Selection) {
    return {
        startX: selection.startX < selection.endX ? selection.startX : selection.endX,
        startY: selection.startY < selection.endY ? selection.startY : selection.endY,
        endX: selection.startX < selection.endX ? selection.endX : selection.startX,
        endY: selection.startY < selection.endY ? selection.endY : selection.startY
    };
}

export function normalizeSelectionCoordinates(selection: Selection): SelectionGeometry {

    return {
        start: selection.state === 'SELECTING' ? selection.start.start
            : selection.start.start > selection.end.start ? selection.end.start : selection.start.start,
        top: selection.start.note.index < selection.end.note.index ? selection.end.note.index : selection.start.note.index,
        left: selection.state === 'SELECTING' ? selection.start.start
            : selection.start.start > selection.end.start ? selection.end.start : selection.start.start,
        width: Math.abs(selection.end.start - selection.start.start),
        height: Math.abs(selection.end.note.index - selection.start.note.index),
    };
}

const monitorCopyDropArea = (
    start: number,
    note: Note,
    noteRange: NoteRangeType,
    setCopyDropOverlayGeometry: ReturnType<typeof useSetCopyDropOverlayGeometry>,
    e: React.MouseEvent<HTMLDivElement>,
    snapToGrid?: {
        ppq: number,
        zoomFactor: number,
        gridResoltion: GridResolution,
        timeSignature: TimeSignature
    },
    cb?: CopyBuffer
) => {
    if (!cb) return;
    if (cb.getState() === 'DROP_PREPARE') {

        const g = cb.getSelection().getGeometry();
        const rect = e.currentTarget.getBoundingClientRect();
        const v = {
            start: start,
            left: 0 | (rect.left / NOTE_WIDTH),
            top: rect.top,
            topNote: note.index,
            width: g.width * NOTE_WIDTH,
            height: g.height * NOTE_HEIGHT
        };

        setCopyDropOverlayGeometry(v);
    }
};

function monitorMouseMove(
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    noteRange: NoteRangeType,
    hoverRef: React.RefObject<HTMLDivElement | null>,
    hnc: HorizontalNoteContainer,
    selection: Selection,
    copyBuffer: CopyBuffer | undefined,
    setCopyDropOverlayGeometry: ReturnType<typeof useSetCopyDropOverlayGeometry>,
    snapToGrid: boolean,
    gridResolution: GridResolution,
    newNoteDuration: NoteDuration,
    zoomFactor: number = 1,
    timeSignature: TimeSignature,
) {
    e.stopPropagation();
    e.preventDefault();
    const coord = convertCoordinates(noteRange, pluckCoordinates(e), zoomFactor);
    const note = coord.note;
    const tick = coord.tick;

    if (selection.state === 'IDLE' || selection.state === 'COMMITTED') {

        // FIXME what is this doing?
        if (hnc && hnc.get(tick).map(n => n.payload.index).includes(note)) {
            return;
        }

        if (hoverRef.current && newNoteDuration && hnc) {
            const newNoteWidth =  newNoteDuration.value * hnc.getPPQ() / zoomFactor ;

            hoverRef.current.style.left = `${tick * NOTE_WIDTH / zoomFactor}px`;
            hoverRef.current.style.top = `${(noteRange.max - note - 1) * NOTE_HEIGHT}px`;
            hoverRef.current.style.width = `${newNoteWidth}px`;
            hoverRef.current.style.height = `${NOTE_HEIGHT}px`;
            hoverRef.current.style.display = 'block';
        }
    } else if (selection.state === 'SELECTING') {
        selection.endX = e.clientX - e.currentTarget.getBoundingClientRect().x;
        selection.endY = e.clientY - e.currentTarget.getBoundingClientRect().y;
        selection.end = {
            note: Note.fromIndex(note),
            start: tick
        };

        if (hoverRef.current) {
            const { startX, startY, endX, endY } = normalizeBoxCoordinates(selection);
            hoverRef.current.style.left = `${startX}px`;
            hoverRef.current.style.top = `${startY}px`;
            hoverRef.current.style.width = `${Math.abs(selection.endX - selection.startX)}px`;
            hoverRef.current.style.height = `${Math.abs(selection.endY - selection.startY)}px`;
        }
    }

    monitorCopyDropArea(
        tick,
        Note.fromIndex(note),
        noteRange,
        setCopyDropOverlayGeometry,
        e,
        snapToGrid ? {
            ppq: hnc.getPPQ(),
            zoomFactor,
            gridResoltion: gridResolution,
            timeSignature: timeSignature
        } : undefined,
        copyBuffer
    );
}

function handleClick(
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    noteRange: NoteRangeType,
    hoverRef: React.RefObject<HTMLDivElement | null>,
    hnc: HorizontalNoteContainer,
    gridResolution: GridResolution,
    snapToGrid: boolean = true,
    setSelectionGroup?: (selection: NoteSelectionGroup | undefined) => void,
    addNoteToContainer?: (tick: number, note: Note) => void,
    selection?: Selection,
    zoomFactor: number = 1,
    timeSignature?: TimeSignature,
    noteSelectionGroup?: NoteSelectionGroup,
) {
    if (selection) {
        const commitedInRealSelection = selection.state === 'COMMITTED' && (selection.endX !== 0 || selection.endY !== 0);
        if (commitedInRealSelection) return;
    }

    e.stopPropagation();
    e.preventDefault();
    const coord = convertCoordinates(noteRange, pluckCoordinates(e), zoomFactor);
    const noteIndex = coord.note;

    const tick = snapToGrid
        ? getLeftNearestGridPoint(coord.tick, hnc.getPPQ(), zoomFactor, gridResolution, timeSignature)
        : coord.tick;

    if (workspaceStore.getState().uiState.editMode === 'NOTES') {
        if (hoverRef.current) {
            // forces overlay to disappear.
            setSelectionGroup?.(undefined);
            hoverRef.current.style.display = 'none';
        }
        const note = Note.fromIndex(noteIndex);
        addNoteToContainer?.(tick, note);
        return;
    }

    const note = hnc.get(tick).find(n => n.payload.index === noteIndex);
    if (!note) return;

    // const _nsg = inputPlaneStore.getState().noteSelectionGroups.get();
    if (noteSelectionGroup) {
        noteSelectionGroup.addNote(tick, note.payload, note.duration, note.velocity);
        setSelectionGroup?.(noteSelectionGroup);
        return;
    }

    const nsg = new NoteSelectionGroup(hnc, {
        start: tick,
        left: tick,
        top: note.payload.index,
        width: 1,
        height: 1
    }, [{
        step: tick,
        payload: note.payload,
        duration: note.duration,
        velocity: note.velocity
    }]);

    setSelectionGroup?.(nsg);
}

function handleDragStart(
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    noteRange: NoteRangeType,
    hoverRef: React.RefObject<HTMLDivElement | null>,
    selection: Selection,
    setSelection: (selection: Selection) => void,
    zoomFactor: number,
    snapToGrid: boolean,
    gridResolution: GridResolution,
) {
    e.stopPropagation();
    e.preventDefault();

    if (selection.state !== 'IDLE') return;

    const { tick, note } = convertCoordinates(noteRange, pluckCoordinates(e), zoomFactor);
    selection.state = 'SELECTING';
    selection.start = {
        note: Note.fromIndex(note),
        start: tick
    };
    selection.startX = e.clientX - e.currentTarget.getBoundingClientRect().x;
    selection.startY = e.clientY - e.currentTarget.getBoundingClientRect().y;

    if (hoverRef.current) {
        hoverRef.current.style.left = `${tickToPosition(NOTE_WIDTH, zoomFactor, tick)}px`;
        hoverRef.current.style.top = `${(107 - note - noteRange.min - 1) * NOTE_HEIGHT}px`;
    }
    setSelection({ ...selection });
}

function handleDragEnd(
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    noteRange: NoteRangeType,
    hoverRef: React.RefObject<HTMLDivElement | null>,
    selection: Selection,
    setSelection: (selection: Selection) => void,
    zoomFactor: number,
) {
    if (selection.state !== 'SELECTING') return;
    selection.state = 'COMMITTED';

    e.stopPropagation();
    e.preventDefault();
    const { tick, note } = convertCoordinates(noteRange, pluckCoordinates(e), zoomFactor);

    // if there was no mouse movement we assume this is a simple click and reset the selection.
    if (tick === selection.start.start && note === selection.start.note.index) {
        // selection.state = 'IDLE';
        setSelection(idleSelection());
        return;
    }

    if (hoverRef.current) {
        const { startX, startY, endX, endY } = normalizeBoxCoordinates(selection);

        hoverRef.current.style.left = `${startX}px`;
        hoverRef.current.style.top = `${startY}px`;
        hoverRef.current.style.width = `${Math.abs(selection.endX - selection.startX)}px`;
        hoverRef.current.style.height = `${Math.abs(selection.endY - selection.startY)}px`;
    }

    setSelection({ ...selection });
}

const TEMPLATE: Selection = {
    state: 'IDLE',
    start: {
        note: Note.fromIndex(0),
        start: 0
    },
    end: {
        note: Note.fromIndex(0),
        start: 0
    },
    startX: 0,
    startY: 0,
    endX: 0,
    endY: 0,
};

function idleSelection(): Selection {
    return { ...TEMPLATE };
}


export function PianoRollBackground({
    trackId,
    hnc,
    harmonicPredicates,
    timeSignature,
    width,
    height,
    noteRange,
    addNoteToContainer,
}: {
    trackId: number,
    hnc?: AbstractHorizontalContainer<any>,
    harmonicPredicates?: HarmonicPredicates,
    timeSignature: TimeSignature,
    width?: number,
    height?: number,
    noteRange: NoteRangeType,
    addNoteToContainer?: (tick: number, note: Note) => void,
}) {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [backgroundData, setBackgroundData] = React.useState<string>();
    const hoverRef = useRef<HTMLDivElement>(null);
    const [selection, setSelection] = React.useState<Selection>(idleSelection());
    const setWorkspaceSelectionGroup = useSetNoteSelectionGroups();
    const copyBuffer = useCopyBuffer();
    const setCopyDropOverlayGeometry = useSetCopyDropOverlayGeometry();
    const zoomFactor = useZoomFactor();
    const workspaceUiState = useWorkspaceUIState();

    const setSelectionGroups = useSetNoteSelectionGroups();
    const noteSelectionGroups = useNoteSelectionGroups();

    if (!hnc) {
        width = width || 800;
        height = height || 600;
    } else {
        width = tickToPosition(NOTE_WIDTH, zoomFactor, hnc.getLength());
        height = (noteRange.max - noteRange.min) * NOTE_HEIGHT;
    }

    useEffect(() => {
        if (!canvasRef.current) return;

        const canvas = canvasRef.current;
        const context = canvas.getContext('2d');
        const normalizedBarWidth = calculateNormalizedBarWidth(timeSignature, hnc);

        if (!context) return;

        drawPianoRollBackground(
            context,
            canvas,
            noteRange,
            harmonicPredicates,
            timeSignature,
            hnc,
            normalizedBarWidth / zoomFactor,
            undefined,
        );

        drawPianoRollGridlines(
            context,
            canvas,
            hnc?.getPPQ() || 480,
            zoomFactor,
            workspaceUiState.gridResolution,
            timeSignature,
        );

        setBackgroundData(canvas.toDataURL());

    }, [hnc, harmonicPredicates, width, height, selection, zoomFactor]);

    useEffect(() => {
        if (!hnc || !(hnc instanceof HorizontalNoteContainer) || !setSelectionGroups) return;
        if (selection.state === 'COMMITTED') {
            const selectionCoordinates = normalizeSelectionCoordinates(selection);

            const nsg = new NoteSelectionGroup(hnc, selectionCoordinates);
            const notes = calculateSelectedNotes(hnc, selectionCoordinates, selection);

            notes.forEach(n => {
                nsg.addNote(n.start, n.note.payload, n.note.duration, n.note.velocity);
            });
            noteSelectionGroups.set(trackId, nsg);
            setSelectionGroups(new Map(noteSelectionGroups));
            setWorkspaceSelectionGroup(new Map(noteSelectionGroups));
            const s = idleSelection();
            setSelection(s);
        }
    }, [selection]);

    const hover = <div ref={hoverRef}
        style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: `${NOTE_WIDTH}px`,
            height: `${NOTE_HEIGHT}px`,
            outline: '1px solid green'
        }} />;

    const canvasWidth2Bars = calculateNormalizedBarWidth(timeSignature, hnc) * 2 * NOTE_WIDTH / zoomFactor;
    const mouseCallbacks = hnc instanceof HorizontalNoteContainer
        ? {
            onMouseMove: (e) => monitorMouseMove(
                e,
                noteRange,
                hoverRef,
                hnc,
                selection,
                copyBuffer,
                setCopyDropOverlayGeometry,
                workspaceUiState.snapToGrid,
                workspaceUiState.gridResolution,
                workspaceUiState.newNoteDuration,
                zoomFactor,
                timeSignature),
            onClick: (e) => handleClick(
                e,
                noteRange,
                hoverRef,
                hnc,
                workspaceUiState.gridResolution,
                workspaceUiState.snapToGrid,
                (g: NoteSelectionGroup | undefined) => {
                    if (!g) {
                        noteSelectionGroups.delete(trackId);
                    } else {
                        noteSelectionGroups.set(trackId, g);
                    }
                    setSelectionGroups(new Map(noteSelectionGroups));
                    setWorkspaceSelectionGroup(new Map(noteSelectionGroups));
                    return;
                },
                addNoteToContainer,
                selection,
                zoomFactor,
                timeSignature),
            onMouseDown: (e) => handleDragStart(
                e,
                noteRange,
                hoverRef,
                selection,
                setSelection,
                zoomFactor,
                workspaceUiState.snapToGrid,
                workspaceUiState.gridResolution),
            onMouseUp: (e) => handleDragEnd(
                e,
                noteRange,
                hoverRef,
                selection,
                setSelection,
                zoomFactor)
        }
        : {};

    return (
        <div
            style={{
                position: 'relative',
                width: `${width}px`,
                height: `${height}px`,
                ...backgroundData
                    ? {
                        backgroundImage: `url(${backgroundData})`,
                        backgroundRepeat: 'repeat-x',
                    }
                    : { display: 'none' }
            }}
            {...mouseCallbacks}
        >
            <canvas
                ref={canvasRef}
                width={canvasWidth2Bars}
                height={height}
            />
            {hover}
        </div>
    );
};

